"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import type { Locale } from "@/lib/i18n-config";
import {
  getHomePath,
  getLocalizedPath,
  getYachtsBuyPath,
  getYachtsComparePath,
  getYachtsModelsPath,
} from "@/lib/routing";

/** Q-IA4 / FH-90-04: 5-item bar on `/yachts/**` only. */
export type YachtsBottomNavLabels = {
  home: string;
  models: string;
  compare: string;
  search: string;
  consult: string;
  aria: string;
};

interface YachtsBottomNavProps {
  locale: Locale;
  labels: YachtsBottomNavLabels;
}

export default function YachtsBottomNav({ locale, labels }: YachtsBottomNavProps) {
  const pathname = usePathname();
  const items = [
    { label: labels.home, href: getHomePath(locale) },
    { label: labels.models, href: getYachtsModelsPath(locale) },
    { label: labels.compare, href: getYachtsComparePath(locale) },
    { label: labels.search, href: getLocalizedPath("/search", locale) },
    { label: labels.consult, href: getYachtsBuyPath(locale) },
  ];

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-[100] border-t border-neutral-200 bg-white md:hidden pb-[env(safe-area-inset-bottom)]"
      aria-label={labels.aria}
      data-nav="yachts-bottom"
    >
      <ul className="flex items-stretch h-14 w-full min-w-0">
        {items.map(({ label, href }) => {
          const active = pathname === href || pathname.startsWith(`${href}/`);
          return (
            <li key={href} className="min-w-0 flex-1 basis-0">
              <Link
                href={href}
                title={label}
                className={`flex h-full min-w-0 flex-col items-center justify-center px-0.5 text-center text-[10px] leading-tight font-medium sm:text-xs ${
                  active ? "text-[var(--color-primary)]" : "text-muted"
                }`}
              >
                <span className="line-clamp-2 w-full break-words hyphens-auto">{label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
